import React, { ReactElement, useCallback, useState, Fragment } from 'react';
import { Checkbox, FadeAndSlideTransition } from '@framework-controls/core';
import * as css from './MaintenanceMenuPanel.module.scss';
import MoreDetails from '../../MoreDetails/containers/MoreDetails';
import parse from 'html-react-parser';
import { strings } from 'src/localization';
import { CheckedConditionalMenu, MaintenanceMenuRec, ApptOps, OpType, UpdateApptOpsInp, DeteremineDeptInp, DepartmentSpec, ascCtaInteractionInput, VehicleRec, ascElementConfigurationInput, ApptVeh, SpecData } from 'src/models';
import ToastBar from 'src/components/ToastBar/containers/ToastBar';
import Information from '../../Information/Information';
import { ascCtaInteraction, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
import { sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IProps {
    menus: MaintenanceMenuRec[];
    checkedMileageMenu: string;
    checkedConditionalMenus: CheckedConditionalMenu[];
    id: string;
    apptOps: ApptOps[];
    specDefaultDept: string;
    specData?: DepartmentSpec;
    oemServices?: string,
    userDefServices?: string,
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    theme?: string;
    apptVeh: ApptVeh;
    vehicleSpecs?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    toggleMenu: (id: string) => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    determineDept: (inp: DeteremineDeptInp) => void;
}

const MaintenanceMenuPanel = (props: IProps) => {
    // determine which menu we are dealing with
    const menuIndex = props.menus.findIndex(result => result.id === props.id);
    let menuChecked = false;
    if (props.checkedMileageMenu === props.id) {
        menuChecked = true;
    } else {
        menuChecked = props.checkedConditionalMenus.some(result => (result.id === props.id) ? true : false);
    }
    const [toastFlag, setToastFlag] = useState<boolean>(false);
    const noStartingAt = (props.theme === 'lexus' || props.theme === 'lexusMonogram') ? true : false;

    const isSDAnalyticsEnabled =
        props.vehicleSpecs?.toyotaTrackingFlag === 'Y' ||
        props.vehicleSpecs?.lexusTrackingFlag === 'Y' ||
        props.vehicleSpecs?.mbzTrackingFlag === 'Y' ||
        props.vehicleSpecs?.mazdaCATrackingFlag === 'Y' ||
        props.vehicleSpecs?.vwTrackingFlag === 'Y';

    // set the display cost to show the customer
    let costRange = strings.CallDealerForPricing;
    if ((props.menus[menuIndex].itemCostLow !== '****') && (props.menus[menuIndex].itemCostHigh !== '****')) {
        costRange = '$' + (Number(props.menus[menuIndex].itemCostLow) / 100).toFixed(2);
        if (props.menus[menuIndex].itemCostLow !== props.menus[menuIndex].itemCostHigh) {
            const costRangeLow = '$' + (Number(props.menus[menuIndex].itemCostLow) / 100).toFixed(2);
            const costRangeHigh = '$' + (Number(props.menus[menuIndex].itemCostHigh) / 100).toFixed(2);
            costRange = costRangeLow + ' - ' + costRangeHigh;
        }
        if (props.menus[menuIndex].itemCostLow !== '****' && props.menus[menuIndex].itemCostHigh === undefined) {
            if (noStartingAt) {
                costRange = `$${(Number(props.menus[menuIndex].itemCostLow) / 100).toFixed(2)}**`;
            } else {
                costRange = `${strings.StartingAt} $${(Number(props.menus[menuIndex].itemCostLow) / 100).toFixed(2)}`;
            }
        }
        if (props.menus[menuIndex].itemCostLow === undefined && props.menus[menuIndex].itemCostHigh !== '****') {
            costRange = `${strings.UpTo} $${(Number(props.menus[menuIndex].itemCostHigh) / 100).toFixed(2)}`;
        }
    }

    const rebuildOps = useCallback(() => {
        let menuOpType = OpType.ConditionalMaintMenu;
        if (props.menus[menuIndex].type === 'mileage') {
            menuOpType = OpType.MaintenanceMenu;
        }
        const opsArrayCopy: UpdateApptOpsInp[] = [...props.apptOps];
        const opsArray: UpdateApptOpsInp[] = [];
        opsArrayCopy.forEach((op) => {
            if ((op.opType !== OpType.ConditionalMaintMenu) && (menuOpType !== OpType.MaintenanceMenu)) {
                opsArray.push(op);
            } else {
                if ((op.opType !== menuOpType)) {
                    opsArray.push(op);
                }
                if (
                    (op.opType === menuOpType) &&
                    (op.menuID !== props.menus[menuIndex].id)
                ) {
                    opsArray.push(op);
                }
            }
        });
        if (!menuChecked) {
            opsArray.push(
                {
                    id: 'maintMenu' + props.menus[menuIndex].id,
                    operation: menuOpType === OpType.MaintenanceMenu ? 'MMENU' : 'CMENU',
                    concern: props.menus[menuIndex].title,
                    estTime: '',
                    menuID: props.menus[menuIndex].id,
                    spgOp: '',
                    opType: menuOpType,
                    itemCostLow: props.menus[menuIndex].itemCostLow,
                    itemCostHigh: props.menus[menuIndex].itemCostHigh,
                    department: props.menus[menuIndex].dept,
                    opWaiter: '',
                    opLoaner: '',
                    opRental: '',
                    opConcierge: '',
                    opCadillac: '',
                    opPPUDWL: '',
                },
            );
        }
        props.UpdateApptOps(opsArray);
        const defaultDept = props.specDefaultDept;
        const determineDept = {
            defaultDept: defaultDept,
        };
        props.determineDept(determineDept);
    }, [
        props.apptOps,
        props.UpdateApptOps,
        props.determineDept,
        props.menus,
        props.specDefaultDept,
        menuChecked,
        menuIndex,
    ]);

    // define the onToggle method
    // Display the Service Added Toast bar
    const onToggle = (e: any) => {
        props.toggleMenu(props.id);
        rebuildOps();
        setToastFlag(true);
        setTimeout(setToastFlag, 2000);
        let elementText = '';
        const selectedMenu = props.menus.find(menu => menu.id === props.id);
        if (selectedMenu) {
            elementText = selectedMenu.title;
        }
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: elementText,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: menuChecked ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: elementText,
            elementType: 'item_details',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: menuChecked ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceSelectionStart', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceVehicleMake: props.apptVeh.make,
                scheduleServiceVehicleMileage: props.apptVeh.mileage,
                scheduleServiceVehicleModel: props.apptVeh.model,
                scheduleServiceVehicleVin: props.apptVeh.vin,
                scheduleServiceVehicleYear: props.apptVeh.vehYr,
            });
        }
    };

    // Set the variant style to be used by the checkbox control based on if it
    // is currently checked or not.
    let cbkClassName = 'primaryCkb';
    if (menuChecked) {
        cbkClassName = 'primaryCkbChecked';
    }

    // count the number of menu items (operations)
    let menuItems: ReactElement[] | null = null;
    let dealerItems: ReactElement[] | null = null;
    let menuItemsWithDetail: ReactElement[] | null = null;
    let dealerItemsWithDetail: ReactElement[] | null = null;
    let numItems = 0;
    let itemsLiteral = strings.Items + ':';
    const oemSrvLit = props.oemServices !== '' ? (props.oemServices + ':') : (props.oemServices);
    const userDefServLit = props.userDefServices !== '' ? (props.userDefServices + ':') : (props.userDefServices);
    numItems = props.menus[menuIndex].operations.length;
    // set the item/items screen literal
    if (numItems === 1) {
        itemsLiteral = strings.Item + ':';
    }
    menuItems = props.menus[menuIndex].operations.filter(op => (op.type !== 'D' && op.type)).map((menuItem, index) => <div
        key={props.id + menuItem.opDesc + index}
        className={css.menuItem}
    >{menuItem.opDesc}</div>);

    dealerItems = props.menus[menuIndex].operations.filter(op => (op.type === 'D' || !op.type)).map((menuItem, index) => <div
        key={props.id + menuItem.opDesc + index}
        className={css.menuItem}
    >{menuItem.opDesc}
    </div>);

    const helpStyles = classNames(
        css.help,
        {
            [css.helpBMW]: props.theme === 'bmw',
        },
    );

    const detailsSalesToolStyles = classNames(
        css.detailsSalesTool,
        {
            [css.detailsSalesToolBMW]: props.theme === 'bmw',
            [css.detailsSalesToolFord]: props.theme === 'ford',

        },
    );

    menuItemsWithDetail = props.menus[menuIndex].operations.filter(op => (op.type !== 'D' && op.type))
        .map((menuItem, index) => <span key={props.id + menuItem.opDesc + index}>
            <div
                className={helpStyles}>
                {menuItem.opDesc}
                <div style={{ display: menuItem.why || menuItem.what ? 'flex' : 'none' }}>
                    <Information
                        headerLit={strings.OperationDetails}
                        vehicles={props.vehicles}
                        selectedVehicle={props.selectedVehicle}
                    >
                        <div
                            className={css.details}>
                            <div className={css.detailsTitle}>
                                <h4 id='h4LightBox'>{menuItem.opDesc}</h4>
                            </div>
                            <div style={{ display: menuItem.why ? 'block' : 'none' }}>
                                <div className={detailsSalesToolStyles}>
                                    {strings.WhyDoINeedThis}
                                </div>
                                <div className={css.detailsText}>
                                    <Fragment>{menuItem.why ? parse(menuItem.why) : ''}</Fragment>
                                </div>
                            </div>
                            <div style={{ display: menuItem.what ? 'block' : 'none' }}>
                                <div className={detailsSalesToolStyles}>
                                    {strings.WhatIsThisFor}
                                </div>
                                <div className={css.detailsText}>
                                    <Fragment>{menuItem.what ? parse(menuItem.what) : ''}</Fragment>
                                </div>
                            </div>
                        </div>
                    </Information>
                </div>
            </div>
        </span>);

    dealerItemsWithDetail = props.menus[menuIndex].operations.filter(op => (op.type === 'D' || !op.type))
        .map((menuItem, index) => <span key={props.id + menuItem.opDesc + index}>
            <div
                className={helpStyles}>
                {menuItem.opDesc}
                <div style={{ display: menuItem.why || menuItem.what ? 'flex' : 'none' }}>
                    <Information
                        headerLit={strings.OperationDetails}
                        vehicles={props.vehicles}
                        selectedVehicle={props.selectedVehicle}
                    >
                        <div
                            className={css.details}>
                            <div className={css.detailsTitle}>
                                <h4 id='h4LightBox'>{menuItem.opDesc}</h4>
                            </div>
                            <div style={{ display: menuItem.why ? 'block' : 'none' }}>
                                <div className={css.detailsSalesTool}>
                                    {strings.WhyDoINeedThis}
                                </div>
                                <div className={css.detailsText}>
                                    <Fragment>{menuItem.why ? parse(menuItem.why) : menuItem.why} </Fragment>
                                </div>
                            </div>
                            <div style={{ display: menuItem.what ? 'block' : 'none' }}>
                                <div className={css.detailsSalesTool}>
                                    {strings.WhatIsThisFor}
                                </div>
                                <div className={css.detailsText}>
                                    <Fragment>{menuItem.what ? parse(menuItem.what) : menuItem.what}</Fragment>
                                </div>
                            </div>
                        </div>
                    </Information>
                </div>
            </div>
        </span>);

    const dealerServicesClassnames = classNames(
        css.dealerServices,
        {
            [css.dealerServicesSecondary]: props.theme === 'acura-CA' || props.theme === 'chevrolet' || props.theme === 'bmw',
            [css.dealerServicesBuick]: props.theme === 'buick',
            [css.dealerServicesHonda]: props.theme === 'honda',
            [css.dealerServicesBMW]: props.theme === 'bmw',
            [css.dealerServicesFord]: props.theme === 'ford',


        },
    );

    let specialServices: ReactElement | null = null;
    if (dealerItems.length > 0) {
        specialServices =
            <div>
                <div className={dealerServicesClassnames}>
                    {userDefServLit}
                </div>
                <div className={css.menuItems}>
                    {dealerItems}
                </div>
            </div>;
    }

    let specialServicesWithDetail: ReactElement | null = null;
    if (dealerItemsWithDetail.length > 0) {
        specialServicesWithDetail =
            <div>
                <div className={css.detailsSpecialHeader}>
                    {userDefServLit}
                </div>
                <div className={css.menuItems}>
                    {dealerItemsWithDetail}
                </div>
            </div>;
    }

    let detailsArea: ReactElement | null = null;

    const detailsMenuPriceStyles = classNames(
        css.detailsMenuPrice,
        {
            [css.detailsMenuPriceBMW]: props.theme === 'bmw',
            [css.detailsMenuPriceFord]: props.theme === 'ford',

        },
    );

    const detailsHeaderStyles = classNames(
        css.detailsHeader,
        {
            [css.detailsHeaderBMW]: props.theme === 'bmw',
            [css.detailsHeaderFord]: props.theme === 'ford',

        },
    );

    const detailsSpecialHeaderStyles = classNames(
        css.detailsSpecialHeader,
        {
            [css.detailsSpecialHeaderBMW]: props.theme === 'bmw',
            [css.detailsSpecialHeaderFord]: props.theme === 'ford',

        },
    );

    const menuCheckBoxItemsStyles = classNames(
        css.menuCheckBoxItems,
        {
            [css.menuCheckBoxItemsFord]: props.theme === 'ford',
        },
    );

    const menuPriceStyles = classNames(
        css.menuPrice,
        {
            [css.menuPriceFord]: props.theme === 'ford',
        },
    );

    if (props.menus[menuIndex].operations.length === 0) {
        detailsArea = <div>{strings.DetailsNotAvailable}</div>;
    } else {
        detailsArea = (
            <div>
                <div onClick={onToggle}>
                    <h4 id={props.theme === 'acura-CA' || props.theme === 'chevrolet' || props.theme === 'honda' ||
                        props.theme === 'buick' || props.theme === 'bmw' ? 'h4CardSecondary' : 'h4Card'}>
                        {props.menus[menuIndex].title}
                    </h4>
                    <div className={css.menuCheckBoxRow}>
                        <div className={menuCheckBoxItemsStyles}>
                            {strings.ThisMaintenanceIncludes} {props.menus[menuIndex].operations.length} {itemsLiteral}
                        </div>
                    </div>
                    {menuItems.length > 0 ? <div className={dealerServicesClassnames}>
                        {oemSrvLit}
                    </div> : ''}
                    <div className={css.menuItems}>
                        {menuItems}
                    </div>
                    {specialServices}
                    <div className={props.theme === 'acura-CA' || props.theme === 'chevrolet' || props.theme === 'buick' ||
                    props.theme === 'bmw' ?
                        menuPriceStyles + ' ' + css.menuPriceSecondary : menuPriceStyles }>
                        {costRange}
                    </div>
                </div>
                <MoreDetails
                    headerLit={strings.MenuScheduleDetails}
                    message={'MENUS'}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}>
                    <div className={css.details}>
                        <div className={css.detailsTitle}>
                            <h4 id='h4LightBox'>{props.menus[menuIndex].title}</h4>
                        </div>
                        <div className={detailsMenuPriceStyles}>
                            {costRange}
                        </div>
                        <div className={detailsHeaderStyles}>
                            {strings.ThisMaintenanceIncludes} {props.menus[menuIndex].operations.length} {itemsLiteral}
                        </div>
                        { menuItemsWithDetail.length > 0 ? <div className={detailsSpecialHeaderStyles}>
                            {oemSrvLit}
                        </div> : ''}
                        <div className={css.menuItems}>
                            {menuItemsWithDetail}
                        </div>
                        {specialServicesWithDetail}
                        <div className={css.detailsText}>
                            {props.specData?.maintMenuDisc}
                        </div>
                        <div className={css.detailsText}>
                            {strings.PricingDisclaimer}
                        </div>
                        <div className={css.detailsText}>
                            {(noStartingAt && costRange.includes('**')) ? strings.PricingDislaimerSPM : null}
                        </div>
                    </div>
                </MoreDetails>
            </div>
        );
    }

    const classes = css.menuPanel + ' ' + (menuChecked ? css.selected : '');

    return (
        <div className={classes}>
            <div style={{width: '100%'}}>
                {detailsArea}
            </div>
            <div id={'menuCheckboxDiv'} className={css.menuCheckBox} onClick={onToggle}>
                <Checkbox
                    id={'menuCheckbox'}
                    size={'rg'}
                    variant={cbkClassName}
                    checked={menuChecked}
                >
                </Checkbox>
                <FadeAndSlideTransition
                    in={toastFlag}
                    timeout={{
                        enter: 100,
                        exit: 100,
                    }}
                    isTop={menuChecked}
                    zIndex={1000}>
                    <ToastBar type='Service' message={props.menus[menuIndex].title} />
                </FadeAndSlideTransition>
            </div>
        </div>
    );
};

export default MaintenanceMenuPanel;